import { Injectable, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarConfig as MatSnackBarConfig
} from '@angular/material/legacy-snack-bar';
import { FormGroupState } from 'ngrx-forms';
import { BehaviorSubject, Subject, Observable, combineLatest, Subscription } from 'rxjs';
import { isEmpty as _isEmpty } from 'lodash';

import { markAsTouchedDeep } from '@sd-portal/shared/functions';
import {
  ValidationSummary,
  getAllErrors,
  getAllNgrxErrors,
  getErrorSummaryCount
} from '@sd-portal/shared/validation';
import { AppFormErrorSummarySheetComponent } from '../app-form-error-summary-sheet/app-form-error-summary-sheet.component';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({ providedIn: 'root' })
export class AppSaveService implements OnDestroy {
  private readonly form: BehaviorSubject<FormGroup | FormGroupState<any> | undefined> =
    new BehaviorSubject(undefined);
  private readonly hasCancel: Subject<boolean> = new Subject<boolean>();
  private readonly executeCancel: Subject<void> = new Subject();
  private readonly executeSave: Subject<void> = new Subject();
  private readonly appSaveUnsubscribe: Subject<void> = new Subject();
  private readonly isSaving = new BehaviorSubject<boolean>(false);
  private readonly disableSave = new BehaviorSubject<boolean>(true);
  private readonly disableSaveButton = new BehaviorSubject<boolean>(true);

  private readonly registeredSaveDisablers = new Array<Observable<boolean>>();
  private saveDisablerSubscription: Subscription;
  // validation
  private readonly validate = new Subject();
  private readonly ngrxControlSelected = new Subject<string>();
  private readonly submitted = new BehaviorSubject(false);

  form$ = this.form.asObservable();
  hasCancel$ = this.hasCancel.asObservable();
  executeCancel$ = this.executeCancel.asObservable();
  executeSave$ = this.executeSave.asObservable();
  appSaveUnsubscribe$ = this.appSaveUnsubscribe.asObservable();
  isSaving$ = this.isSaving.asObservable();
  disableSave$ = this.disableSave.asObservable();
  disableSaveButton$ = this.disableSaveButton.asObservable();

  // validation
  validate$ = this.validate.asObservable();
  ngrxControlSelected$ = this.ngrxControlSelected.asObservable();
  submitted$ = this.submitted.asObservable();

  disableOverride = new BehaviorSubject<boolean>(false);
  disableOverride$ = this.disableOverride.asObservable();

  primaryChanged = new Subject<string>();
  primaryChanged$ = this.primaryChanged.asObservable();

  constructor(
    private readonly bottomSheet: MatBottomSheet,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnDestroy(): void {}

  setDisabledState(isDisabled: boolean) {
    this.disableSaveButton.next(isDisabled);
  }

  setDisableOverride(isDisabled: boolean) {
    this.disableOverride.next(isDisabled);
  }

  exeSave() {
    this.executeSave.next();
  }

  exeCancel() {
    this.executeCancel.next();
  }

  setForm(form?: FormGroup | FormGroupState<any>) {
    this.form.next(form);
  }

  destroyForm() {
    this.appSaveUnsubscribe.next();
    this.submitted.next(false);
    this.setForm();
  }

  setCancel(hasCancel: boolean = false) {
    this.hasCancel.next(hasCancel);
  }

  setIsSaving(isSaving: boolean = true) {
    this.isSaving.next(isSaving);
  }

  validateForm() {
    let form = this.form.getValue();

    if (form) {
      this.submitted.next(true);
      this.validate.next();

      const isReactiveForm = form instanceof FormGroup;
      const isNgrxForm = 'isValid' in form;
      let errors: ValidationSummary[] = [];

      if (isReactiveForm) {
        form = form as FormGroup;
        markAsTouchedDeep(form);
        errors = getAllErrors(form);
      }

      if (isNgrxForm) {
        form = form as FormGroupState<any>;
        errors = getAllNgrxErrors(form);
      }

      if (errors.length) {
        this.openValidationSnackBar(errors);
      }
    }
  }

  selectNgrxControlId(id: string) {
    this.ngrxControlSelected.next(id);
  }

  registerSaveDisabler(saveDisablingObservable: Observable<boolean>) {
    this.registeredSaveDisablers.push(saveDisablingObservable);
    if (this.saveDisablerSubscription) {
      this.saveDisablerSubscription.unsubscribe();
    }
    this.saveDisablerSubscription = combineLatest(this.registeredSaveDisablers).subscribe(
      disablers => {
        let saveDisabled = false;
        for (const shouldDisable of disablers) {
          if (shouldDisable) {
            saveDisabled = true;
            break;
          }
        }
        this.disableSave.next(saveDisabled);
      }
    );
  }

  private openValidationSnackBar(errors: ValidationSummary[]) {
    const errorCount = getErrorSummaryCount(errors);
    const snackBarConfig: MatSnackBarConfig = {
      panelClass: ['sdmat-snack-bar', 'sdmat-snack-bar-validation'],
      verticalPosition: 'top',
      duration: 5000
    };

    this.snackBar
      .open(`${errorCount} Errors found`, 'View Validation Summary', snackBarConfig)
      .onAction()
      .subscribe(() => {
        this.openValidationSheet(errors);
      });
  }

  private openValidationSheet(errors: ValidationSummary[]) {
    const data = [...errors];
    this.bottomSheet.open(AppFormErrorSummarySheetComponent, { data });
  }

  primaryTypeChanged(formType: string) {
    this.primaryChanged.next(formType);
  }
}
